import { IconSuccess } from 'anchor-ui/icons';
import defaultBg from "../../assets/backgrounds/chat-background.jpg";
import auroraBg from "../../assets/backgrounds/aurora.avif";
import mistyForestBg from "../../assets/backgrounds/misty-forest.avif";
import oldTechBg from "../../assets/backgrounds/old-tech.avif";
import mountainsBg from "../../assets/backgrounds/mountains.avif";
import sunsetBg from "../../assets/backgrounds/sunset.avif";
import { useRef, useState } from 'react';
import { Functions, httpsCallable } from 'firebase/functions';
import { checkUserIsRoyal } from '../../utils/checkSubscription';
import GoogleAdSense from '../GoogleAdSense';
import { getSecondaryColor } from '../../utils/activeColor';
import Card from "anchor-ui/card"

const ThemePage = ({
  activeColorState,
  handleSetActiveColor,

  secondaryColorState,
  handleSetSecondaryColor,

  backgroundImageState,
  handleSetBackgroundImage,

  loggedInUser,

  setAlert,
  functions,

  setNeedSubscriptionWarning,
}: {
  activeColorState: string,
  handleSetActiveColor: (color: string) => void,

  secondaryColorState: string,
  handleSetSecondaryColor: (color: string) => void,

  backgroundImageState: string,
  handleSetBackgroundImage: (image: string) => void,

  loggedInUser: CometChat.User,

  setAlert: React.Dispatch<React.SetStateAction<{ message: string; type: string; } | null>>,
  functions: Functions,

  setNeedSubscriptionWarning: React.Dispatch<React.SetStateAction<React.ReactNode | null>>;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isRoyal = checkUserIsRoyal(loggedInUser.getRole());

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Trigger file input click
  const triggerFileInput = () => {
    if (!isRoyal) {
      setNeedSubscriptionWarning(
        <>
          <b>Je hebt een Royal abonnement nodig om je eigen afbeelding te uploaden.</b>
          <p>Upgrade nu naar Royal voor meer mogelijkheden!</p>
        </>
      );
      return;
    }


    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const file = event.target.files[0];

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setAlert({ message: 'Afbeelding mag maximaal 2MB zijn', type: 'error' });
      return;
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      setIsLoading(true);

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async () => {
        const base64Data = reader.result as string;

        try {
          // Call the Firebase function to upload the background image
          const uploadBackgroundImage = httpsCallable(functions, 'uploadBackgroundImage');
          const result = await uploadBackgroundImage({
            imageData: {
              base64Data,
              filename: file.name,
              contentType: file.type
            }
          });

          const data = result.data as { success: boolean, backgroundUrl: string };

          if (data.success) {
            // Update the background image state in the parent component
            handleSetBackgroundImage(data.backgroundUrl);

            setAlert({ message: 'Achtergrond is succesvol geüpload', type: 'success' });
          }
        } catch (error) {
          console.error('Error in upload operation:', error);
          setAlert({ message: 'Er is iets misgegaan bij het uploaden van de achtergrond', type: 'error' });
        } finally {
          setIsLoading(false);
        }
      };

      reader.onerror = () => {
        setAlert({ message: 'Er is iets misgegaan bij het verwerken van de afbeelding', type: 'error' });
        setIsLoading(false);
      };
    } catch (error) {
      console.error('Error uploading background image:', error);
      setAlert({ message: 'Er is iets misgegaan bij het uploaden van de achtergrond', type: 'error' });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card style={{ height: '100%', width: '100%', backgroundColor: getSecondaryColor() }}>
        <GoogleAdSense
          adSlot="9000114933"
          style={{ display: 'inline-block', width: "984px", height: "121px" }}
          isRoyal={checkUserIsRoyal(loggedInUser?.getRole() ?? "")}
        />
      </Card>
      <br />

      <div className="flex">
        <div className="responsive-ad">
          <GoogleAdSense
            adSlot="6371157564"
            style={{ display: 'inline-block', width: "121px", height: "984px", paddingRight: "16px" }}
            isRoyal={checkUserIsRoyal(loggedInUser?.getRole() ?? "")}
          />
        </div>

        <div>
          <h2>Kies een thema</h2>

          <div className="theme-selection">
            <button
              className={`blue-button ${activeColorState === '#177FBF' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#177FBF')}
            >
              ChatPlaza
              {activeColorState === '#177FBF' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`orange-button ${activeColorState === '#f2922c' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#f2922c')}
            >
              Oranje
              {activeColorState === '#f2922c' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`yellow-button ${activeColorState === '#fbc02d' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#fbc02d')}
            >
              Geel
              {activeColorState === '#fbc02d' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`green-button ${activeColorState === '#22ac55' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#22ac55')}
            >
              Groen
              {activeColorState === '#22ac55' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`purple-button ${activeColorState === '#5d3c85' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#5d3c85')}
            >
              Paars
              {activeColorState === '#5d3c85' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`red-button ${activeColorState === '#d32f2f' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#d32f2f')}
            >
              Rood
              {activeColorState === '#d32f2f' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`pink-button ${activeColorState === '#e91e63' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#e91e63')}
            >
              Roze
              {activeColorState === '#e91e63' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
            <button
              className={`black-button ${activeColorState === '#212121' ? 'active' : ''}`}
              onClick={() => handleSetActiveColor('#212121')}
            >
              Zwart
              {activeColorState === '#212121' && <IconSuccess color={'white'} style={{ width: '100%' }} />}
            </button>
          </div>

          <br /><br />
          <h2>Achtergrondkleur (VIP 💎)</h2>

          <div className="theme-selection">
            <button
              className={`white-button ${secondaryColorState === "white" ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor("white")}
            >
              Wit (standaard)
              {secondaryColorState === "white" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
            <button
              className={`black-button ${secondaryColorState === '#0A0A0A' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#0A0A0A')}
            >
              Darkmode
              {secondaryColorState === '#0A0A0A' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
          </div>

          <br /><br />
          <h2>Chatachtergrond (VIP 💎)</h2>

          <div className="theme-selection chat-background">
            <button
              className={backgroundImageState === "default" ? 'active' : ''}
              onClick={() => handleSetBackgroundImage("default")}
            >
              <img src={defaultBg} alt="Achtergrond" />
              <br /><br />
              Standaard
              {backgroundImageState === "default" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>

            <button
              className={backgroundImageState === "aurora" ? 'active' : ''}
              onClick={() => handleSetBackgroundImage("aurora")}
            >
              <img src={auroraBg} alt="Achtergrond" />
              <br /><br />
              Aurora
              {backgroundImageState === "aurora" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>

            <button
              className={backgroundImageState === "forest" ? 'active' : ''}
              onClick={() => handleSetBackgroundImage("forest")}
            >
              <img src={mistyForestBg} alt="Achtergrond" />
              <br /><br />
              Mistig bos
              {backgroundImageState === "forest" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>

            <button
              className={backgroundImageState === "old-tech" ? 'active' : ''}
              onClick={() => handleSetBackgroundImage("old-tech")}
            >
              <img src={oldTechBg} alt="Achtergrond" />
              <br /><br />
              Oude tech
              {backgroundImageState === "old-tech" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>

            <button
              className={backgroundImageState === "mountains" ? 'active' : ''}
              onClick={() => handleSetBackgroundImage("mountains")}
            >
              <img src={mountainsBg} alt="Achtergrond" />
              <br /><br />
              Bergen
              {backgroundImageState === "mountains" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>

            <button
              className={backgroundImageState === "sunset" ? 'active' : ''}
              onClick={() => handleSetBackgroundImage("sunset")}
            >
              <img src={sunsetBg} alt="Achtergrond" />
              <br /><br />
              Zonsondergang
              {backgroundImageState === "sunset" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
          </div>

          <br /><br />
          <h2>Achtergrondkleur (Royal 👑)</h2>

          <div className="theme-selection">
            <button
              className={`dark-blue-button ${secondaryColorState === '#000022' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#000022')}
            >
              Oceanisch Blauw
              {secondaryColorState === '#000022' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
            <button
              className={`dark-orange-button ${secondaryColorState === '#331a00' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#331a00')}
            >
              Solar Flare
              {secondaryColorState === '#331a00' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
            <button
              className={`dark-yellow-button ${secondaryColorState === '#333300' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#333300')}
            >
              Zonsopgang Geel
              {secondaryColorState === '#333300' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
            <button
              className={`dark-green-button ${secondaryColorState === '#002200' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#002200')}
            >
              Bosgroen
              {secondaryColorState === '#002200' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
            <button
              className={`dark-purple-button ${secondaryColorState === '#1e0033' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#1e0033')}
            >
              Mystiek Paars
              {secondaryColorState === '#1e0033' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
            <button
              className={`dark-red-button ${secondaryColorState === '#330000' ? 'active' : ''}`}
              onClick={() => handleSetSecondaryColor('#330000')}
            >
              Donkerrood
              {secondaryColorState === '#330000' && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>
          </div>

          <br /><br />
          <h2>Chatachtergrond (Royal 👑)</h2>

          <div className="theme-selection">
            <button
              onClick={triggerFileInput}
              className='blue-button'
              disabled={isLoading}
            >
              <br /><br />
              {isLoading ? 'Uploading...' : 'Upload je eigen afbeelding'}
              {backgroundImageState === "default" && <IconSuccess color="white" style={{ width: '100%' }} />}
            </button>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleFileSelection}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ThemePage;
